import { useCallback, useEffect, useState } from 'react';
import { sessionStore } from '../utils/storageWrapper';

// Simplified from https://usehooks-ts.com/react-hook/use-session-storage

// A wrapper for "JSON.parse()"" to support "undefined" value
function parseJSON<T>(value: string | null): T | undefined {
  try {
    return value === 'undefined' ? undefined : JSON.parse(value ?? '');
  } catch {
    return undefined;
  }
}

type UseSessionStorageReturn<T> = [
  value: T,
  setValue: (value: T) => void,
  remove: () => void,
];

const useSessionStorage = <T>(
  /**
   * Set to null or empty to ignore session storage, and just use initialValue.
   * Useful if some usages need sessionStorage and others don't
   */
  key: string | null,
  initialValue: T,
): UseSessionStorageReturn<T> => {
  const readValue = useCallback((): T => {
    if (!key || typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = sessionStore.getItem(key);
      return item ? (parseJSON(item) as T) : initialValue;
    } catch (error) {
      return initialValue;
    }
  }, [initialValue, key]);

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(readValue);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to sessionStorage.
  const setValue = useCallback((value) => {
    // Prevent build error "window is undefined" but keeps working
    if (!key || typeof window === 'undefined') return;

    // Allow value to be a function so we have the same API as useState
    const newValue = value instanceof Function ? value(storedValue) : value;

    // Avoid render loops
    if (storedValue === newValue) return;

    // Save to session storage
    sessionStore.setItem(key, JSON.stringify(newValue));

    // Save state
    setStoredValue(newValue);
  }, [key, storedValue]);

  useEffect(() => {
    if (!key) return;
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = useCallback(() => {
    setValue(undefined);
    if (key) {
      sessionStore.removeItem(key);
    }
  }, [key, setValue]);

  return [storedValue, setValue, remove];
};

export default useSessionStorage;
